import { useCallback, useState } from "react";

/**
 * Filter state that only affects queries after explicit commit (Apply button).
 * Mirrors {@link useCommittedSearch} for toolbar filters in large list views.
 */
export function useCommittedFilters<T>(
  emptyState: T,
  isEqual: (a: T, b: T) => boolean,
  initialApplied?: T,
) {
  const initial = initialApplied ?? emptyState;
  const [draft, setDraft] = useState<T>(initial);
  const [applied, setApplied] = useState<T>(initial);

  const commit = useCallback(() => {
    setApplied(draft);
  }, [draft]);

  const clear = useCallback(() => {
    setDraft(emptyState);
    setApplied(emptyState);
  }, [emptyState]);

  const syncDraftFromApplied = useCallback(() => {
    setDraft(applied);
  }, [applied]);

  const hasUnapplied = !isEqual(draft, applied);

  return {
    draft,
    setDraft,
    applied,
    setApplied,
    commit,
    clear,
    syncDraftFromApplied,
    hasUnapplied,
  };
}
